import { useRef } from "react";

import {
    getLoopWindTransaction,
    getLoopWindTransactionAndBalanceChanges,
    IncreaseLeverageTransactionInput,
    convertLoanValueTupleToBps,
    DEFAULT_WEIGHT_MATRIX
} from "@bridgesplit/abf-sdk";
import {
    AnalyticsInfo,
    BalanceChanges,
    Result,
    TransactionGenerationType,
    uiAmountToLamports,
    decimalsToBps
} from "@bridgesplit/utils";

import { useAbfFetches } from "../reducers";
import {
    AbfTransactionDetails,
    AbfGeneratorResult,
    LoopExpanded,
    TransactionSenderOptions,
    TransactionsWithBalanceChanges,
    AbfLoanExpanded
} from "../types";
import {
    useAbfGenerateTransactionWithData,
    buildBundledTransactionBatch,
    wrapBundledAsAbfGeneratorResult
} from "./common";
import { useCreateSimulationPromise } from "./simulation";
import { getZcCollateral, useUserTokens } from "../api";

export type LoopWindParams = IncreaseLeverageTransactionInput & {
    loopExpanded: LoopExpanded;
    userPublicKey: string;
    leverageMultiplier: number;
    depositMint: string;
    isInitialWind: boolean;
    analyticsInfo?: AnalyticsInfo;
};

function formatWindTransactionParams(params: LoopWindParams): IncreaseLeverageTransactionInput {
    const { loopExpanded } = params;
    const principalDecimals = loopExpanded.principalToken.decimals;

    return {
        ...params,
        principalRequested: params.principalRequested.map((param) => ({
            ledgerIndex: param.ledgerIndex,
            principalAmount: uiAmountToLamports(param.principalAmount, principalDecimals),
            principalMint: param.principalMint,
            strategy: param.strategy,
            durationIndex: param.durationIndex,
            expectedLoanValues: param.expectedLoanValues
        })),
        depositCollateral: params.depositCollateral.map((collateral) => {
            return {
                collateralAmount: uiAmountToLamports(
                    collateral.collateralAmount,
                    loopExpanded.collateralToken.decimals
                ),
                collateralAssetData: collateral.collateralAssetData,
                loanCreationParams: collateral.loanCreationParams,
                weightMatrixUpdate: collateral.weightMatrixUpdate ?? DEFAULT_WEIGHT_MATRIX
            };
        }),
        isLoop: true
    };
}

export function useLoopWindTransaction(): AbfTransactionDetails<LoopWindParams> & {
    getLastLoanAddress: () => string | undefined;
    getTransactionsWithParamsAndBalanceChanges: (
        params: LoopWindParams
    ) => Promise<Result<TransactionsWithBalanceChanges>>;
} {
    const generate = useAbfGenerateTransactionWithData();
    const { resetNapoleonApi, resetMarketsPublicApi, resetLoopApi, resetLoanApi } = useAbfFetches();
    const { refresh } = useUserTokens();
    const lastLoanAddressRef = useRef<string>();
    const createSimulationPromise = useCreateSimulationPromise();

    async function getTransactionsWithParams({ ...params }: LoopWindParams): AbfGeneratorResult {
        lastLoanAddressRef.current = undefined;
        try {
            const create = generate({
                generateFunction: getLoopWindTransaction,
                identifier: "Create Position",
                firstTxIdentifier: "Setup Accounts",
                onGenerate: (response) => {
                    lastLoanAddressRef.current = response.loanAddress;
                },
                params: formatWindTransactionParams(params),
                analyticsInfo: params.analyticsInfo,
                txnSettingsOverride: {
                    broadcastMode: TransactionGenerationType.Jito
                }
            });

            return wrapBundledAsAbfGeneratorResult(create, createSimulationPromise, "finalized", params.analyticsInfo);
        } catch (error) {
            return Result.err(error);
        }
    }

    async function getTransactionsWithParamsAndBalanceChanges(
        params: LoopWindParams
    ): Promise<Result<TransactionsWithBalanceChanges>> {
        lastLoanAddressRef.current = undefined;

        try {
            let balanceChanges: BalanceChanges | undefined;

            const create = generate({
                generateFunction: getLoopWindTransactionAndBalanceChanges,
                identifier: "Create position",
                firstTxIdentifier: "Setup Accounts",
                onGenerate: (response) => {
                    lastLoanAddressRef.current = response.loanAddress;
                    balanceChanges = response.balanceChanges;
                },
                params: formatWindTransactionParams(params),
                queryParams: { loopIdentifier: params.loopExpanded.vaultIdentifier },
                analyticsInfo: params.analyticsInfo,
                txnSettingsOverride: {
                    broadcastMode: TransactionGenerationType.Jito
                }
            });

            const transactions = await create;
            if (transactions.isErr()) {
                return Result.err(transactions);
            }

            const nonEmptyBalanceChanges =
                balanceChanges && Object.keys(balanceChanges).length > 0 ? balanceChanges : undefined;

            const orderedTransactions = buildBundledTransactionBatch(transactions.unwrap(), "finalized");
            return Result.ok({
                transactions: orderedTransactions,
                balanceChanges: nonEmptyBalanceChanges,
                simulationPromise: createSimulationPromise(orderedTransactions)
            });
        } catch (error) {
            return Result.err(error);
        }
    }

    const sendOptions: TransactionSenderOptions = {
        refetch: () => {
            resetNapoleonApi();
            resetMarketsPublicApi();
            resetLoopApi();
            void refresh();
            resetLoanApi();
        },
        minGeyserConfirmations: 5
    };

    return {
        getTransactionsWithParams,
        getTransactionsWithParamsAndBalanceChanges,
        sendOptions,
        description: "Initializing position",
        getLastLoanAddress: () => lastLoanAddressRef.current
    };
}

/**
 * Format params for manual wind: UI amounts to lamports, expectedApy/expectedLqt to bps.
 * Same shape as formatWindTransactionParams but takes decimals explicitly (no loopExpanded).
 */
export function formatManualIncreaseLeverageParams(
    params: IncreaseLeverageTransactionInput,
    principalDecimals: number,
    collateralDecimals: number
): IncreaseLeverageTransactionInput {
    return {
        ...params,
        principalRequested: params.principalRequested.map((param) => ({
            ledgerIndex: param.ledgerIndex,
            principalAmount: uiAmountToLamports(param.principalAmount, principalDecimals),
            principalMint: param.principalMint,
            strategy: param.strategy,
            durationIndex: param.durationIndex,
            expectedLoanValues: {
                expectedApy: decimalsToBps(param.expectedLoanValues.expectedApy),
                expectedLqt: convertLoanValueTupleToBps(param.expectedLoanValues.expectedLqt)
            }
        })),
        depositCollateral: params.depositCollateral.map((collateral) => ({
            collateralAmount: uiAmountToLamports(collateral.collateralAmount, collateralDecimals),
            collateralAssetData: collateral.collateralAssetData,
            weightMatrixUpdate: collateral.weightMatrixUpdate ?? DEFAULT_WEIGHT_MATRIX
        })),
        isLoop: true
    };
}

/**
 * Manual increase leverage transaction details: build transactions from IncreaseLeverageTransactionInput.
 */
export function useManualIncreaseLeverageTransaction(
    loanExpanded: AbfLoanExpanded | undefined
): AbfTransactionDetails<IncreaseLeverageTransactionInput> {
    const generate = useAbfGenerateTransactionWithData();
    const { resetLoanApi, resetMarketsPublicApi } = useAbfFetches();
    const { refresh } = useUserTokens();
    const createSimulationPromise = useCreateSimulationPromise({ skipSimulate: true });
    const collateral = getZcCollateral(loanExpanded);
    const principalDecimals = loanExpanded?.principalMetadata.decimals;
    const collateralDecimals = collateral?.metadata.decimals;

    async function getTransactionsWithParams(params: IncreaseLeverageTransactionInput): AbfGeneratorResult {
        if (principalDecimals === undefined || collateralDecimals === undefined) {
            return Result.err("Missing decimals");
        }
        const formattedParams = formatManualIncreaseLeverageParams(params, principalDecimals, collateralDecimals);
        return wrapBundledAsAbfGeneratorResult(
            generate({
                generateFunction: getLoopWindTransaction,
                identifier: "Increase Leverage",
                firstTxIdentifier: "Setup Accounts",
                params: formattedParams
            }),
            createSimulationPromise
        );
    }

    const sendOptions: TransactionSenderOptions = {
        refetch: () => {
            resetLoanApi();
            resetMarketsPublicApi();
            void refresh();
        }
    };

    return { getTransactionsWithParams, sendOptions, description: "Managing position" };
}

export function useLoopWindRefetch() {
    const { resetNapoleonApi, resetMarketsPublicApi, resetLoopApi, resetLoanApi } = useAbfFetches();
    const { refresh } = useUserTokens();

    return () => {
        resetNapoleonApi();
        resetMarketsPublicApi();
        resetLoopApi();
        void refresh();
        resetLoanApi();
    };
}
